import { useEffect, useMemo, useRef, useState } from "react";
import { FileUploadRef } from "../../../shared/components/FileUpload";

import { usePhase } from "../../projectOverview/hooks/usePhase";

import { useGetQueryDetails } from "../mutation";
import { queryAPI } from "../api/queryDetails";
import { useSearchProjects } from "../../projects/mutations";

import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

export const useRaiseQuery = ({ id, projectId, editStatus }: { id: null | string; projectId: string | undefined; editStatus: boolean }) => {

    const fileUploadRef = useRef<FileUploadRef | null>(null);

    const [isSaving, setIsSaving] = useState(false);
    const [hasFilesToUpload, setHasFilesToUpload] = useState(false);
    const handleFileUploadCountChange = (files: any[]) => setHasFilesToUpload(files.length > 0);

    const searchProjects = useSearchProjects();

    const { queryDetails, isFetching, isPending, isError, removeCache } = useGetQueryDetails({ queryId: id! });

    const [query, setQuery] = useState<{
        title: null | string,
        category: null | string,
        type: 'Project' | 'Portfolio' | 'Programme',
        priority: null | string,
        description: null | string,
        phase: null | string,
        assigned: null | string,
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
    }>({
        title: null,
        category: null,
        type: 'Project',
        priority: null,
        description: null,
        phase: null,
        assigned: null,
        project: null,
        portfolio: null,
        programme: null,
    });

    const [queryErrors, setQueryErrors] = useState<{
        title: null | string,
        category: null | string,
        type: null | string,
        priority: null | string,
        description: null | string,
        phase: null | string,
        assigned: null | string,
        project: null | string,
        portfolio: null | string,
        programme: null | string, 
        }>({
            title: null,
            category: null,
            type: null,
            priority: null,
            description: null,
            phase: null,
            assigned: null,
            project: null,
            portfolio: null,
            programme: null,
        });

    const [isUploading, setIsUploading] = useState(false);

        const validateQuery = () => {
            let errors: any = {};
            if (!query.title) {
                errors.title = 'Title is required';
            }
            if (!query.category) {
                errors.category = 'Category is required';
            }
            if (!query.priority) {
                errors.priority = 'Priority is required';
            }
            if (!query.description) {
                errors.description = 'Description is required';
            }
            if (!query.assigned) {
                errors.assigned = 'Assigned is required';
            }
            if (!query.type) {
                errors.type = 'Type is required';
            }
            if (query.type === 'Portfolio' && !query.portfolio) {
                errors.portfolio = 'Portfolio is required';
            }
            if (query.type === 'Programme' && !query.programme) {
                errors.programme = 'Programme is required';
            }
            if (query.type === 'Project' && !query.project) {
                errors.project = 'Project is required';
            }
            if (query.type === 'Project' && !query.phase) {
                errors.phase = 'Phase is required';
            }
            setQueryErrors(errors);
            return Object.keys(errors).length === 0;
        };


    const activeProjectId = useMemo(() => {
        return query.project?.id ?? projectId;
    }, [query.project?.id, projectId]);
    
    const phaseData = usePhase(activeProjectId ? String(activeProjectId) : undefined);

    const handleUpdate = (key: string, value: any) => {
        setQuery({
            ...query,
            [key]: value
        })
        setQueryErrors({
            ...queryErrors,
            [key]: null
        })
    };

    const params = () => {
        const payload: any = {
            title: query.title,
            description: query.description,
            category: query.category,
            priority: query.priority,
            user_id: query.assigned,
            type: query.type
        };
        if (query.project) {
            payload.project_id = query.project.id;
        };
        if (query.portfolio) {
            payload.portfolio_id = query.portfolio.id;
        };
        if (query.phase) {
            payload.project_tree_id = query.phase;
        };
        if (query.programme) {
            payload.programme_id = query.programme.id;
        };
        return payload;
    };

    const saveQuery = async () => {
        const payload = params();
        if (editStatus && id) {
            return await queryAPI.update({ queryId: id, payload });
        }
        return await queryAPI.create({ payload });
    };

    const onSave = async (onSaved: ()=> void) => {
        if (!validateQuery()) return;
        setIsSaving(true);

        try {
            const response = await saveQuery();
            const queryId = response?.id ?? response?.data?.id;

            if (!queryId) {
                throw new Error("Query save response did not return an id.");
            }

            if (fileUploadRef.current) {
                fileUploadRef.current.setId(queryId);
                setIsUploading(true);
                try {
                    await fileUploadRef.current.uploadAllFiles(queryId);
                } finally {
                    setIsUploading(false);
                }
            }

            removeCache();
            onSaved();
        } catch (error) {
            console.error("RaiseQuery save failed:", error);
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setQuery({
            ...query,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        });
    };

    useEffect(() => {
        if (projectId && !editStatus)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (editStatus && queryDetails) {
            setQuery({
                title: queryDetails.title,
                category: queryDetails.category,
                type: queryDetails.type,
                priority: queryDetails.priority,
                description: queryDetails.description,
                phase: queryDetails.project_tree_id,
                assigned: queryDetails.user_id,
                project: queryDetails.project_id ? { id: queryDetails.project_id, label: queryDetails.project_title } : null,
                portfolio: queryDetails.portfolio_id ? { id: queryDetails.portfolio_id, label: queryDetails.portfolio_name } : null,
                programme: queryDetails.programme_id ? { id: queryDetails.programme_id, label: queryDetails.programme_name } : null,
            });
        }
    }, [editStatus, queryDetails, isFetching, isPending, isError]);

    return {
        query,
        queryErrors,
        handleUpdate,
        onSave,
        fileUploadRef,
        phaseData,
        onFileUploadCountChange: handleFileUploadCountChange,
        isPending: isSaving || (!!activeProjectId && phaseData.isPending) || isUploading,
    };

};